import * as React from "react";
import { type MatchMode } from "../../models/ColumnFilter";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import WatchableValue from "../../models/WatchableValue";
import EditSelect from "../EditSelect";
import type { ColumnConfig } from "./UniversalObjectTable";
import { itx } from "../../translationItx/itx";

export type ColumnFilterProps = {
  className?: string;
  columnConfig: ColumnConfig;
  selectId: string;
  style?: React.CSSProperties;
  opts?: { preserveOptions?: boolean };
  onFilterChange: (columnConfig: ColumnConfig) => void;
};

function matchModeFromValue(v: string): MatchMode {
  if (v === "__N") return "Name";
  if (v === "__T") return "Tag";
  return "All"; // no itx
}

function valueFromMatchMode(mode: MatchMode): string {
  if (mode === "Name") return "__N"; // no itx
  if (mode === "Tag") return "__T"; // no itx
  return "__A"; // no itx
}

/** Spaltenfilter-Dropdown für Tabellen-Header (MatchMode Name/Tag/All + EditSelect). */
export default function ColumnFilterUI(props: ColumnFilterProps) {
  const { className, selectId, style, onFilterChange } = props;
  const { columnConfig } = props;
  const columnFilter = columnConfig.filter;

  useOnChangeWatcher(columnFilter, (changedProperty, changedObject) => {
    const xRedraw = changedProperty != "filterExpression";
    return xRedraw;
  });

  const filter = columnFilter;

  const modeLabel =
    filter.matchMode === "Name"
      ? itx("ColumnFilterUI.name", "Name")
      : filter.matchMode === "Tag"
        ? itx("ColumnFilterUI.tag", "Tag")
        : itx("ColumnFilterUI.all", "All");

  if (!filter.options) {
    filter.options = filter.getOptions?.();
  }

  const xOptions = filter.matchMode !== "Name" ? filter.options || [] : [];

  //  "Nach Tag oder Name filtern"
  const blank = "\u00A0\u00A0"; // no itx
  const check = "\u2713 "; // Unicode CHECK MARK (✓) // no itx
  const match_mode_options: Array<{ value: string; label: string }> = [
    {
      value: "__A", // no itx
      label:
        itx("ColumnFilterUI.filtertagorname", "Nach Tag oder Name filtern") +
        blank +
        (columnFilter.matchMode === "All" ? check : ""),
    },
    {
      value: "__T", // no itx
      label:
        itx("ColumnFilterUI.filterbytag", "Nach Tag filtern") + blank + (columnFilter.matchMode === "Tag" ? check : ""),
    },
    {
      value: "__N", // no itx
      label:
        itx("ColumnFilterUI.filterbyname", "Nach Name filtern") +
        blank +
        (columnFilter.matchMode === "Name" ? check : ""),
    },
  ];

  const hasInputText = !!(filter.filterExpression && !filter.filterExpression.startsWith("__"));
  const editSelectOptions = React.useRef(new WatchableValue<Array<{ value: string; label: string }>>([]));
  editSelectOptions.current.value = [
    ...match_mode_options,
    ...xOptions.map((option) => ({ value: option, label: option })),
  ];

  if (!filter.active) {
    return (
      <div className={className} style={{ paddingLeft: "5px", paddingRight: "5px", color: "white" }}>
        {columnConfig.title}
      </div>
    );
  }

  return (
    <div
      className={"columnFilter estadaRow " + (hasInputText ? "hasInputText " : "") + (className || "")}
      style={style}
    >
      <div
        className={"filterIcon"}
        data-tooltip-content={itx("ColumnFilterUI.filter", "Filter: ") + modeLabel}
        data-tooltip-place="top-end" // no itx
        data-tooltip-id="tv-tooltip" // no itx
      >
        <i className="las la-filter" />
        <div>{filter.matchMode === "Name" ? "N" : filter.matchMode === "Tag" ? "T" : ""}</div>
      </div>

      <EditSelect
        className="white"
        id={selectId}
        value={filter.filterExpression}
        placeholder={columnConfig.title}
        onChange={(v) => {
          if (v.startsWith("__")) {
            columnFilter.filterExpression = "";
            columnFilter.matchMode = matchModeFromValue(v);
            columnFilter.raiseOnChange("filterExpression");
          } else {
            columnFilter.filterExpression = v;
            columnFilter.raiseOnChange("filterExpression");
          }
          onFilterChange(columnConfig);
        }}
        options={editSelectOptions.current}
        style={{ flexGrow: "1" }}
      />
    </div>
  );
}
